import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { CmLawBrandBar } from '../../components/branding/CmLawBrandBar'
import { cn } from '../../lib/utils'

type YesNoValue = '' | 'yes' | 'no'

function YesNoField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: YesNoValue
  onChange: (value: YesNoValue) => void
}) {
  return (
    <fieldset className="space-y-2">
      <legend id={id} className="text-sm font-medium leading-snug text-slate-900">{label}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={id}>
        {(['yes', 'no'] as const).map((option) => (
          <button
            key={option}
            type="button"
            id={option === 'yes' ? `${id}-yes` : `${id}-no`}
            role="radio"
            aria-checked={value === option}
            onClick={() => onChange(option)}
            className={cn(
              'min-w-[4.5rem] rounded-md border px-4 py-2 text-sm font-medium transition-colors',
              value === option
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
            )}
          >
            {option === 'yes' ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

export default function IntakeForm() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [stateCounty, setStateCounty] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [employerName, setEmployerName] = useState('')
  const [stillWorking, setStillWorking] = useState<YesNoValue>('')
  const [severanceOffered, setSeveranceOffered] = useState<YesNoValue>('')
  const [experiencedMisconduct, setExperiencedMisconduct] = useState<YesNoValue>('')
  const [protectedCharacteristic, setProtectedCharacteristic] = useState<YesNoValue>('')
  const [blewWhistle, setBlewWhistle] = useState<YesNoValue>('')
  const [medicalLeave, setMedicalLeave] = useState<YesNoValue>('')
  const [factsDescription, setFactsDescription] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    navigate(`/intake/${token}/success`, { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <CmLawBrandBar />
      <div className="mx-auto max-w-xl px-4 py-6">
        <p className="mb-6 text-center text-sm text-slate-600">Client intake (prototype)</p>

        <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div>
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              name="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              className="mt-1"
              autoComplete="name"
            />
          </div>

          <div>
            <Label htmlFor="stateCounty">What State and County do you live in?</Label>
            <Input
              id="stateCounty"
              name="stateCounty"
              value={stateCounty}
              onChange={(e) => setStateCounty(e.target.value)}
              placeholder="New Jersey, Bergen County"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="mt-1"
              autoComplete="tel"
            />
          </div>

          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              className="mt-1"
              autoComplete="email"
            />
          </div>

          <div>
            <Label htmlFor="employerName">What is the name of the employer?</Label>
            <Input
              id="employerName"
              name="employerName"
              value={employerName}
              onChange={(e) => setEmployerName(e.target.value)}
              placeholder="Company LLC"
              className="mt-1"
            />
          </div>

          <YesNoField
            id="stillWorking"
            label="Are you still working for the employer?"
            value={stillWorking}
            onChange={setStillWorking}
          />

          <YesNoField
            id="severanceOffered"
            label="Have you been offered a severance agreement?"
            value={severanceOffered}
            onChange={setSeveranceOffered}
          />

          <YesNoField
            id="experiencedMisconduct"
            label="Did you experience harassment, discrimination, sexual harassment or retaliation?"
            value={experiencedMisconduct}
            onChange={setExperiencedMisconduct}
          />

          <YesNoField
            id="protectedCharacteristic"
            label="Was the harassment, discrimination or retaliation based on a protected characteristic like race, gender, religion, age, national origin, pregnancy, sexual orientation?"
            value={protectedCharacteristic}
            onChange={setProtectedCharacteristic}
          />

          <YesNoField
            id="blewWhistle"
            label="Did you recently blow the whistle on illegal conduct?"
            value={blewWhistle}
            onChange={setBlewWhistle}
          />

          <YesNoField
            id="medicalLeave"
            label="Did you recently take a medical leave of absence?"
            value={medicalLeave}
            onChange={setMedicalLeave}
          />

          <div>
            <Label htmlFor="factsDescription">Describe all of the facts surrounding your matter:</Label>
            <Textarea
              id="factsDescription"
              name="factsDescription"
              value={factsDescription}
              onChange={(e) => setFactsDescription(e.target.value)}
              rows={6}
              className="mt-1"
              placeholder="Please include dates, people involved, and what happened."
            />
          </div>

          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
            Submit
          </Button>
        </form>
      </div>
    </div>
  )
}
